import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { AlertOctagon, Check, Eye, FolderInput, Loader2, Minus, Pencil, Plus, RefreshCw, RotateCcw, Save, Server, Trash2, X } from 'lucide-react';
import {
  DEFAULT_MAX_CONCURRENT_IMPORTS, PROGRAMME_VARIANT_KEY, SIDECAR_ASSET_CLASS_TARGET, SIDECAR_ASSET_ID_TARGET, SIDECAR_MASTER_OF_TARGET, SIDECAR_MASTER_VARIANT_TARGET,
  WatchFolderSettingsSchema, type FieldDefinition, type WatchFolderDTO, type WatchFolderItemDTO, type WatchFolderSettings,
} from '@prism/contracts';
import { api, errorMessage } from '../../api/client';
import { useAgents, useCategories, useMasterVariants, useQcProfiles, useRightsSettings, useSchema, useStorage, useWatchFolderItems, useWatchFolders } from '../../api/hooks';
import { RightsDefaultsBlock } from './RightsDefaults';
import { rightsDefaultsSummary, rightsFieldIds } from './rightsDefaultsText';
import { Modal } from '../../components/Modal';
import { QcReviewTaskFields } from '../../components/QcReviewTask';
import { toast } from '../../components/Toasts';
import { EmptyState } from '../../components/EmptyState';
import { bytes, relative } from '../../lib/format';
import { FolderPicker } from '../../components/FolderPicker';
import { queryKeys } from '../../api/queryKeys';

const DEFAULT_SETTINGS: WatchFolderSettings = WatchFolderSettingsSchema.parse({});

export function WatchFoldersAdmin() {
  const categoryList = useCategories().data?.items ?? [];
  const categoryName = (id: string) => categoryList.find((c) => c.id === id)?.name ?? 'Category';
  const variantList = useMasterVariants().data?.items ?? [];
  // 1.8.0: the folder's rights defaults on its card (while rights management is on).
  const rightsOn = Boolean(useRightsSettings().data?.enabled);
  const cardSchema = useSchema().data?.definition;
  const q = useWatchFolders();
  const qc = useQueryClient();
  const [editing, setEditing] = useState<WatchFolderDTO | 'new' | null>(null);
  const [selected, setSelected] = useState<string | null>(null);
  const items = q.data?.items ?? [];
  const current = items.find((f) => f.id === selected) ?? null;

  async function scan(f: WatchFolderDTO) {
    try {
      const r = await api<{ ingested: number; duplicates: number; failed: number; waiting: number; queued?: number }>(`/api/v1/watch-folders/${f.id}/scan`, { method: 'POST', json: {} });
      toast(`Scanned ${f.name}: ${r.ingested} ingested, ${r.waiting} waiting to settle${r.queued ? `, ${r.queued} waiting for an import slot` : ''}${r.failed ? `, ${r.failed} failed` : ''}`);
      await qc.invalidateQueries({ queryKey: queryKeys.watchFolders() });
      await qc.invalidateQueries({ queryKey: queryKeys.watchFolderItems(f.id) });
    } catch (err) {
      toast(errorMessage(err), 'error');
    }
  }

  async function remove(f: WatchFolderDTO) {
    if (!window.confirm(`Stop watching “${f.name}”? Files in the folder are not touched.`)) return;
    try {
      await api(`/api/v1/watch-folders/${f.id}`, { method: 'DELETE' });
      if (selected === f.id) setSelected(null);
      await qc.invalidateQueries({ queryKey: queryKeys.watchFolders() });
      toast(`Removed ${f.name}`);
    } catch (err) {
      toast(errorMessage(err), 'error');
    }
  }

  return (
    <div className="page" data-testid="watch-folders">
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 12 }}>
        <h2 style={{ margin: 0 }}>Watch folders</h2>
        <span style={{ flex: 1 }} />
        <button className="btn primary" onClick={() => setEditing('new')} data-testid="new-watch-folder"><Plus size={14} /> New watch folder</button>
      </div>
      <p className="lede">Drop material into a watch folder and PRISM ingests it once the files stop changing. Each folder has its own import settings: proxies, XML sidecar metadata, attachments and subtitles, and whether files are moved or copied into storage.</p>
      {q.isLoading ? <div className="skeleton" style={{ height: 120 }} /> : items.length === 0 ? (
        <EmptyState style={{ minHeight: 180 }} icon={<FolderInput size={24} />} title="No watch folders yet">
          <p>Create one on a server folder{q.data?.mountsRoot ? '' : ' (set PRISM_MOUNTS_ROOT first)'} or on a storage agent’s root.</p></EmptyState>
      ) : (
        <div className="cards">
          {items.map((f) => (
            <div className="panel-card" key={f.id} data-testid="watch-folder-card" style={f.enabled ? undefined : { opacity: 0.7 }}>
              <h3>{f.kind === 'agent' ? <Server size={16} color="var(--accent-strong)" /> : <FolderInput size={16} color="var(--accent-strong)" />} {f.name}</h3>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                <span className="pill">{f.settings.afterIngest === 'move' ? 'Moves into storage' : 'Copies into storage'}</span>
                {f.settings.categoryId && <span className="pill" data-testid="wf-category-pill">{categoryName(f.settings.categoryId)}</span>}
                {f.settings.masterVariantId && <span className="pill" data-testid="wf-variant-pill">{variantList.find((v) => v.id === f.settings.masterVariantId)?.name ?? 'Master variant'}</span>}
                {!f.settings.proxy && <span className="pill">No proxies</span>}
                {f.settings.qc?.enabled && <span className="pill accent" data-testid="wf-qc-pill">QC</span>}
                {f.settings.live && <span className="pill" style={{ color: '#ff6b6f' }}>Live ingest</span>}
                {(f.settings.sidecarXml || f.settings.sidecarJson) && <span className="pill">{[f.settings.sidecarXml && 'XML', f.settings.sidecarJson && 'JSON'].filter(Boolean).join(' + ')} sidecars</span>}
                {f.settings.attachments && <span className="pill">Attachments</span>}
                {!f.enabled && <span className="pill">Paused</span>}
              </div>
              {rightsOn && rightsDefaultsSummary(cardSchema, f.settings.defaults, f.settings.rightsDates, 'arrival') && (
                <span className="faint" data-testid="wf-rights-summary">© {rightsDefaultsSummary(cardSchema, f.settings.defaults, f.settings.rightsDates, 'arrival')}</span>
              )}
              <dl className="kv">
                <dt>Watches</dt><dd className="mono">{f.kind === 'agent' ? `${f.agent?.name} · ${f.agent?.path !== null && f.agent?.path !== undefined ? `watch root/${f.agent.path}` : `root ${f.agent?.root}`}` : f.path}</dd>
                {f.agent && <><dt>Agent</dt><dd>{f.agent.connected ? 'Connected' : 'Disconnected'} · site {f.agent.site}</dd></>}
                <dt>Stores in</dt><dd>{f.destination?.name ?? 'Hi-res originals tier'}</dd>
                <dt>Imports</dt><dd>{f.counts.ingested} ingested{f.counts.duplicate ? ` · ${f.counts.duplicate} skipped` : ''}{f.counts.failed ? <span style={{ color: 'var(--danger)' }}> · {f.counts.failed} failed</span> : ''}{f.counts.importing ? ` · ${f.counts.importing} importing` : ''}</dd>
                {/* 1.0.0-rc.13: in flight and waiting for an import slot, as in Monitor → Ingest. */}
                {(f.counts.inFlight || f.counts.waiting) ? <><dt>Now</dt><dd data-testid="wf-queue">Importing {f.counts.inFlight ?? 0}{f.counts.waiting ? ` · waiting ${f.counts.waiting.toLocaleString('en-US')}` : ''} (at most {f.settings.maxConcurrentImports ?? DEFAULT_MAX_CONCURRENT_IMPORTS} at a time)</dd></> : null}
                {f.counts.inFolder !== null && <><dt>In the folder now</dt><dd>{f.counts.inFolder} file{f.counts.inFolder === 1 ? '' : 's'} already imported</dd></>}
                <dt>Last scan</dt><dd>{relative(f.lastScanAt)}</dd>
                {f.lastError && <><dt>Problem</dt><dd style={{ color: 'var(--danger)' }}>{f.lastError}</dd></>}
              </dl>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                <button className="btn sm" onClick={() => setSelected(f.id)} data-testid="watch-folder-activity"><Eye size={13} /> Activity</button>
                {f.kind === 'server' && <button className="btn ghost sm" onClick={() => void scan(f)}><RefreshCw size={13} /> Scan now</button>}
                <button className="btn ghost sm" onClick={() => setEditing(f)}><Pencil size={13} /> Edit</button>
                <button className="btn ghost sm danger" onClick={() => void remove(f)}><Trash2 size={13} /> Remove</button>
              </div>
            </div>
          ))}
        </div>
      )}
      {current && <Activity folder={current} onClose={() => setSelected(null)} />}
      {editing && <WatchFolderDialog folder={editing === 'new' ? null : editing} onClose={() => setEditing(null)} />}
    </div>
  );
}

function StatusIcon({ status }: { status: WatchFolderItemDTO['status'] }) {
  // waiting (1.0.0-rc.13): stable, waiting for an import slot; shown like a queued step.
  const cls = status === 'ingested' ? 'succeeded' : status === 'failed' ? 'failed' : status === 'importing' ? 'running' : status === 'waiting' ? 'pending' : 'skipped';
  return (
    <span className={`step-icon ${cls}`}>
      {status === 'ingested' ? <Check size={12} strokeWidth={3} /> : status === 'failed' ? <X size={12} strokeWidth={3} /> : status === 'importing' ? <Loader2 size={12} className="spin" /> : <Minus size={12} />}
    </span>
  );
}

function Activity({ folder, onClose }: { folder: WatchFolderDTO; onClose: () => void }) {
  const q = useWatchFolderItems(folder.id);
  const qc = useQueryClient();
  const items = q.data?.items ?? [];
  async function retry(item: WatchFolderItemDTO) {
    try {
      await api(`/api/v1/watch-folders/${folder.id}/items/${item.id}/retry`, { method: 'POST', json: {} });
      await qc.invalidateQueries({ queryKey: queryKeys.watchFolderItems(folder.id) });
      toast(`${item.path} will be picked up on the next scan`);
    } catch (err) {
      toast(errorMessage(err), 'error');
    }
  }
  return (
    <div className="panel-card" style={{ marginTop: 20 }} data-testid="watch-folder-items">
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <h3 style={{ flex: 1 }}>{folder.name} · recent files</h3>
        <button className="btn ghost sm" onClick={onClose}>Close</button>
      </div>
      {items.length === 0 ? <p className="muted" style={{ margin: 0 }}>Nothing picked up yet.</p> : (
        <div className="steps">
          {items.map((i) => (
            <div className="step" key={i.id} data-testid="watch-item" data-status={i.status}>
              <StatusIcon status={i.status} />
              <div className="step-main">
                <span className="step-title">{i.path} <span className="faint">· {bytes(i.sizeBytes)}</span></span>
                {i.asset && <span className="step-sub">{i.status === 'duplicate' ? 'Already in the library as ' : 'Asset '}<Link to={`/assets/${i.asset.id}`}>{i.asset.title}</Link></span>}
                {i.sidecars.map((s) => <span className="step-sub" key={s.name}>{s.name} · {s.role} · {s.outcome}</span>)}
                {i.notes.map((n) => <span className="step-sub" key={n} style={{ color: 'var(--warning)' }}>{n}</span>)}
                {i.error && <span className="step-sub" style={{ color: 'var(--danger)' }}><AlertOctagon size={11} style={{ verticalAlign: -1 }} /> {i.error}</span>}
              </div>
              {!i.current && <span className="pill" title="This path was imported again later">Earlier import</span>}
              {i.current && i.status !== 'importing' && <button className="btn ghost sm" onClick={() => void retry(i)} data-testid="watch-item-again"><RotateCcw size={12} /> {i.status === 'ingested' ? 'Ingest again' : 'Retry'}</button>}
              <span className="step-time">{relative(i.updatedAt)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Toggle({ label, help, value, onChange, testId }: { label: string; help: string; value: boolean; onChange: (v: boolean) => void; testId?: string }) {
  return (
    <label className="toggle-row" data-testid={testId}>
      <input type="checkbox" checked={value} onChange={(e) => onChange(e.target.checked)} />
      <span><strong>{label}</strong><span className="help" style={{ display: 'block' }}>{help}</span></span>
    </label>
  );
}

function DefaultValue({ field, value, onChange }: { field: FieldDefinition; value: unknown; onChange: (v: unknown) => void }) {
  if (field.type === 'vocabulary' && !field.multiple) {
    return <select className="select" value={(value as string) ?? ''} onChange={(e) => onChange(e.target.value || undefined)}><option value="">—</option>{field.options?.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}</select>;
  }
  if (field.type === 'boolean') return <select className="select" value={value === true ? 'yes' : value === false ? 'no' : ''} onChange={(e) => onChange(e.target.value === '' ? undefined : e.target.value === 'yes')}><option value="">—</option><option value="yes">Yes</option><option value="no">No</option></select>;
  const list = field.type === 'tags' || field.multiple;
  return <input className="input" type={field.type === 'number' ? 'number' : field.type === 'date' ? 'date' : 'text'} placeholder={list ? 'comma, separated' : field.type === 'datetime' ? '2026-10-20T18:00:00+03:00' : ''}
    value={Array.isArray(value) ? value.join(', ') : value == null ? '' : String(value)}
    onChange={(e) => {
      const t = e.target.value;
      if (!t) return onChange(undefined);
      onChange(list ? t.split(',').map((x) => x.trim()).filter(Boolean) : field.type === 'number' ? Number(t) : t);
    }} />;
}

export function WatchFolderDialog({ folder, onClose, agentId: presetAgent }: { folder: WatchFolderDTO | null; onClose: () => void; agentId?: string }) {
  const qc = useQueryClient();
  const storage = useStorage().data;
  const agents = useAgents().data?.items ?? [];
  const schema = useSchema().data?.definition;
  const categories = useCategories().data?.items ?? [];
  const variants = useMasterVariants().data?.items ?? [];
  const [name, setName] = useState(folder?.name ?? '');
  const [enabled, setEnabled] = useState(folder?.enabled ?? true);
  const [kind, setKind] = useState<'server' | 'agent'>(folder?.kind ?? (presetAgent ? 'agent' : 'server'));
  const [path, setPath] = useState(folder?.path ?? '');
  const [agentId, setAgentId] = useState(folder?.agent?.id ?? presetAgent ?? '');
  const [agentRoot, setAgentRoot] = useState(folder?.agent?.root ?? '');
  // Folders under the agent's watch root; named roots only for folders set up that way before.
  const [agentPath, setAgentPath] = useState(folder?.agent?.path ?? '');
  const legacy = Boolean(folder?.agent?.root && folder.agent.path === null);
  const [destination, setDestination] = useState<string>(folder?.destination?.id ?? '');
  const [s, setS] = useState<WatchFolderSettings>(folder?.settings ?? DEFAULT_SETTINGS);
  const [saving, setSaving] = useState(false);
  const set = <K extends keyof WatchFolderSettings>(k: K, v: WatchFolderSettings[K]) => setS((x) => ({ ...x, [k]: v }));
  const qcProfiles = useQcProfiles().data;
  const fields = schema?.fields ?? [];
  // 1.8.0: while rights are on, the rights fields have their own block and leave the Default metadata grid.
  const rightsOn = Boolean(useRightsSettings().data?.enabled);
  const rightsIds = rightsOn ? rightsFieldIds(schema) : new Set<string>();
  const activeAgents = agents.filter((a) => a.status === 'active');
  const agent = agents.find((a) => a.id === agentId);
  const managed = (l: { agentRoot: { mode: string } | null; agentFolder: { mode: string } | null }) => l.agentRoot?.mode === 'managed' || l.agentFolder?.mode === 'managed';
  const destinations = (storage?.items ?? []).filter((l) => l.enabled && l.purpose === 'storage' && (l.kind !== 'agent' || (managed(l) && (kind === 'server' || l.agent?.id === agentId))));

  // Agent folders: where the proxy is made. Same-agent storage keeps originals on site, so the agent always makes it.
  const destLoc = destination ? storage?.items.find((l) => l.id === destination) : undefined;
  const sameAgent = kind === 'agent' && destLoc?.kind === 'agent' && destLoc.agent?.id === agentId;
  const noFfmpeg = kind === 'agent' && Boolean(agent) && 'ffmpeg' in agent!.capabilities && !agent!.capabilities.ffmpeg;
  const processOnAgent = sameAgent || (s.processOn === 'agent' && !noFfmpeg);

  async function save() {
    setSaving(true);
    try {
      const body = { name: name.trim(), enabled, kind, ...(kind === 'server' ? { path } : legacy ? { agentId, agentRoot } : { agentId, agentPath }), destinationLocationId: destination || null, settings: kind === 'agent' ? { ...s, processOn: processOnAgent ? 'agent' : 'central' } : s };
      await api(folder ? `/api/v1/watch-folders/${folder.id}` : '/api/v1/watch-folders', { method: folder ? 'PUT' : 'POST', json: body });
      await qc.invalidateQueries({ queryKey: queryKeys.watchFolders() });
      toast(folder ? `Saved ${name}` : `Watching ${name}`);
      onClose();
    } catch (err) {
      toast(errorMessage(err), 'error');
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal title={folder ? `Edit ${folder.name}` : 'New watch folder'} wide onClose={onClose} actions={<>
      <button className="btn ghost" onClick={onClose}>Cancel</button>
      <button className="btn primary" disabled={saving || !name.trim() || (kind === 'server' ? !path : !agentId || (legacy ? !agentRoot : false))} onClick={() => void save()} data-testid="save-watch-folder">
        {saving ? <Loader2 size={14} className="spin" /> : <Save size={14} />} {folder ? 'Save' : 'Start watching'}
      </button>
    </>}>
      <div className="form" style={{ gap: 14 }}>
        <div className="field-row" style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 12 }}>
          <div className="field"><label htmlFor="wf-name">Name</label><input id="wf-name" className="input" value={name} onChange={(e) => setName(e.target.value)} maxLength={80} placeholder="e.g. Camera cards" /></div>
          <div className="field"><label>State</label><div className="segmented"><button aria-pressed={enabled} onClick={() => setEnabled(true)}>Watching</button><button aria-pressed={!enabled} onClick={() => setEnabled(false)}>Paused</button></div></div>
        </div>
        <div className="field"><label>Where the folder is</label>
          <div className="segmented" role="radiogroup">
            <button aria-pressed={kind === 'server'} onClick={() => setKind('server')}>Folder on the PRISM server</button>
            <button aria-pressed={kind === 'agent'} onClick={() => setKind('agent')} data-testid="wf-kind-agent">Folder on a storage agent (on-site)</button>
          </div>
          <span className="help">Watch folders live under a separate watch root, apart from storage, so PRISM never ingests from its own storage.</span>
        </div>
        {kind === 'server' ? <FolderPicker path={path} onPath={setPath} source={{ type: 'server', root: 'watch' }} /> : !legacy ? (
          <>
            <div className="field"><label htmlFor="wf-agent">Storage agent</label>
              <select id="wf-agent" className="select" value={agentId} onChange={(e) => { setAgentId(e.target.value); setAgentPath(''); }}>
                <option value="">Choose…</option>
                {activeAgents.map((a) => <option key={a.id} value={a.id}>{a.name} · {a.site}</option>)}
              </select>
              {activeAgents.length === 0 && <span className="help">Enrol an agent in Administration → Storage agents first.</span>}
            </div>
            {agentId && <FolderPicker path={agentPath} onPath={setAgentPath} source={{ type: 'agent', agentId, base: 'watch' }} />}
          </>
        ) : (
          <div className="field-row" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div className="field"><label htmlFor="wf-agent">Storage agent</label>
              <select id="wf-agent" className="select" value={agentId} onChange={(e) => { setAgentId(e.target.value); setAgentRoot(''); }}>
                <option value="">Choose…</option>
                {activeAgents.map((a) => <option key={a.id} value={a.id}>{a.name} · {a.site}</option>)}
              </select>
              {activeAgents.length === 0 && <span className="help">Enrol an agent in Administration → Storage agents first.</span>}
            </div>
            <div className="field"><label htmlFor="wf-root">Root</label>
              <select id="wf-root" className="select" value={agentRoot} onChange={(e) => setAgentRoot(e.target.value)} disabled={!agent}>
                <option value="">Choose…</option>
                {agent?.allowedRoots.filter((r) => {
                  const mode = storage?.items.find((l) => l.agent?.id === agentId && l.agentRoot?.name === r)?.agentRoot?.mode;
                  return mode !== 'managed' && (mode !== 'watch' || folder?.agent?.root === r);
                }).map((r) => <option key={r} value={r}>{r}</option>)}
              </select>
              <span className="help">A root the agent is authorised for, dedicated to ingest.</span>
            </div>
          </div>
        )}
        <div className="field"><label htmlFor="wf-category">Asset class of ingested assets</label>
          <select id="wf-category" className="select" style={{ maxWidth: 320 }} value={s.categoryId ?? ''} onChange={(e) => set('categoryId', e.target.value || undefined)} data-testid="wf-category">
            <option value="">No class</option>
            {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </div>
        {/* 1.2.0: the master variant of what this folder ingests; a sidecar's AssetClass and MasterVariant follow the precedence below. */}
        <div className="field"><label htmlFor="wf-variant">Master variant of ingested files</label>
          <select id="wf-variant" className="select" style={{ maxWidth: 320 }} value={s.masterVariantId ?? ''} onChange={(e) => set('masterVariantId', e.target.value || undefined)} data-testid="wf-master-variant">
            <option value="">Programme</option>
            {variants.filter((v) => v.key !== PROGRAMME_VARIANT_KEY).map((v) => <option key={v.id} value={v.id}>{v.name} ({v.code})</option>)}
          </select>
          <span className="help">The variant of each new asset’s primary master. A sidecar can set the class and the variant too (AssetClass, MasterVariant), and MasterOf adds the file as a further master of an existing asset; “Sidecars override defaults” below decides which wins. <Link to="/help/sidecars#masters">Masters in sidecars</Link></span>
        </div>
        <div className="field-row" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <div className="field"><label htmlFor="wf-dest">Store originals in</label>
            <select id="wf-dest" className="select" value={destination} onChange={(e) => setDestination(e.target.value)}>
              <option value="">Hi-res originals tier (Storage policy)</option>
              {destinations.map((l) => <option key={l.id} value={l.id}>{l.name} · {l.site}</option>)}
            </select>
          </div>
          <div className="field"><label>After ingest</label>
            <div className="segmented">
              <button aria-pressed={s.afterIngest === 'move'} onClick={() => set('afterIngest', 'move')}>Move into storage</button>
              <button aria-pressed={s.afterIngest === 'copy'} onClick={() => set('afterIngest', 'copy')}>Copy, leave files</button>
            </div>
            <span className="help">Files are removed only after the stored copy is verified by checksum.</span>
          </div>
        </div>
        {kind === 'agent' && (
          <div className="field" data-testid="wf-process-on"><label>Make the proxy</label>
            <div className="segmented" role="radiogroup">
              <button aria-pressed={processOnAgent} disabled={noFfmpeg || undefined} onClick={() => set('processOn', 'agent')} data-testid="wf-process-agent">On the storage agent</button>
              <button aria-pressed={!processOnAgent} disabled={sameAgent} onClick={() => set('processOn', 'central')} data-testid="wf-process-central">Centrally</button>
            </div>
            {sameAgent ? <span className="help">Originals stay on site, so the agent makes the proxy.</span>
              : noFfmpeg ? <span className="help" style={{ color: 'var(--warning)' }}>The storage agent has no FFmpeg; install it on the site server to make proxies there.</span>
              : <span className="help">The agent makes the probe and proxy from the file in the watch folder, so only the proxy is needed centrally. Poster, scrub images and waveform are always made centrally from the proxy.</span>}
          </div>
        )}
        <div className="toggle-grid">
          <Toggle label="Make proxies" help="Proxy, poster, scrub images and waveform. Off catalogues and indexes only." value={s.proxy} onChange={(v) => set('proxy', v)} testId="wf-proxy" />
          <Toggle label="Read XML sidecars" help="clip.xml beside clip.mov fills metadata fields." value={s.sidecarXml} onChange={(v) => set('sidecarXml', v)} />
          <Toggle label="Read JSON sidecars" help="clip.json beside clip.mov fills metadata fields." value={s.sidecarJson} onChange={(v) => set('sidecarJson', v)} />
          <Toggle label="Import attachments" help="Other files named like the clip (clip.pdf, clip.psd…) are attached." value={s.attachments} onChange={(v) => set('attachments', v)} />
          <Toggle label="Import subtitle tracks" help="clip.fi.srt / clip.en.vtt become subtitle tracks." value={s.subtitleTracks} onChange={(v) => set('subtitleTracks', v)} />
          <Toggle label="Include subfolders" help="Watch the whole tree below the folder." value={s.includeSubfolders} onChange={(v) => set('includeSubfolders', v)} />
          <Toggle label="Skip duplicates" help="Leave content that is already in the library in the folder." value={s.duplicates === 'skip'} onChange={(v) => set('duplicates', v ? 'skip' : 'link')} />
        </div>
        <div className="field" data-testid="wf-qc">
          <Toggle label="Check quality (QC)" help="Run automated QC on every file this folder ingests, after its proxy is made. Off by default." value={Boolean(s.qc?.enabled)}
            onChange={(v) => set('qc', { ...(s.qc ?? {}), enabled: v })} testId="wf-qc-toggle" />
          {s.qc?.enabled && (
            <div className="field" style={{ maxWidth: 420, marginTop: 6 }}><label htmlFor="wf-qc-profile">QC profile</label>
              <select id="wf-qc-profile" className="select" value={s.qc.profileId ?? ''} onChange={(e) => set('qc', { enabled: true, ...(s.qc?.reviewTask ? { reviewTask: s.qc.reviewTask } : {}), ...(e.target.value ? { profileId: e.target.value } : {}) })} data-testid="wf-qc-profile">
                <option value="">The default profile{qcProfiles ? ` (${qcProfiles.items.find((p) => p.isDefault)?.name ?? ''})` : ''}</option>
                {qcProfiles?.items.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
              <span className="help">Findings appear on the asset’s QC tab and in its QC timecode layer. <Link to="/help/quality-control#watch-folders">QC in watch folders</Link></span>
            </div>
          )}
          {s.qc?.enabled && (
            // The review task when QC of a file from here fails (1.0.0-rc.9): the general setting, none, or this folder's own.
            <div className="field" style={{ maxWidth: 520, marginTop: 6 }}><label htmlFor="wf-qc-review">QC review task</label>
              <select id="wf-qc-review" className="select" value={s.qc.reviewTask?.mode ?? 'default'} data-testid="wf-qc-review"
                onChange={(e) => {
                  const mode = e.target.value as 'default' | 'none' | 'assign';
                  set('qc', { ...s.qc!, reviewTask: mode === 'assign' ? { mode, task: { assignee: { type: 'role', key: 'media_manager' }, dueHours: null, onWarnings: false } } : { mode } });
                }}>
                <option value="default">Use the general setting</option>
                <option value="none">None</option>
                <option value="assign">Assign to…</option>
              </select>
              {s.qc.reviewTask?.mode === 'assign' && (
                <div style={{ marginTop: 6 }}>
                  <QcReviewTaskFields idPrefix="wf-qc" offLabel="None" value={s.qc.reviewTask.task}
                    onChange={(task) => set('qc', { ...s.qc!, reviewTask: task ? { mode: 'assign', task } : { mode: 'none' } })} />
                </div>
              )}
              <span className="help">One task per failed run. The general setting is in Administration → Quality control.</span>
            </div>
          )}
        </div>
        <div className="field" style={{ maxWidth: 260 }}><label htmlFor="wf-stable">Wait until files are unchanged for (seconds)</label>
          <input id="wf-stable" className="input" type="number" min={1} max={3600} value={s.stabilitySeconds} onChange={(e) => set('stabilitySeconds', Math.max(1, Math.min(3600, Number(e.target.value) || 1)))} />
        </div>
        {/* 1.0.0-rc.13: a drop of thousands of files no longer starts thousands of imports at once. */}
        <div className="field" style={{ maxWidth: 260 }}><label htmlFor="wf-max-imports">Import at most this many files at a time</label>
          <input id="wf-max-imports" className="input" type="number" min={1} max={500} value={s.maxConcurrentImports ?? DEFAULT_MAX_CONCURRENT_IMPORTS} data-testid="wf-max-imports"
            onChange={(e) => set('maxConcurrentImports', Math.max(1, Math.min(500, Math.floor(Number(e.target.value)) || DEFAULT_MAX_CONCURRENT_IMPORTS)))} />
          <span className="help">A file counts until its ingest (and QC) has finished. More files wait, oldest first, and start as these finish; nothing is skipped. Growing files of a live folder start at once.</span>
        </div>
        {kind === 'server' && (
          <div className="field">
            <Toggle label="Live ingest" help="Catalogue files while a capture system is still writing them, with a live proxy to watch and log about 6 to 10 seconds behind. Suits growing MXF, MPEG-TS and fragmented MP4. When the file stops growing it is ingested like any other." value={s.live} onChange={(v) => set('live', v)} testId="wf-live" />
            {s.live && (
              <div className="field" style={{ maxWidth: 260, marginTop: 6 }}><label htmlFor="wf-live-close">The recording ends when the file has not grown for (seconds)</label>
                <input id="wf-live-close" className="input" type="number" min={5} max={3600} value={s.liveCloseSeconds} onChange={(e) => set('liveCloseSeconds', Math.max(5, Math.min(3600, Number(e.target.value) || 30)))} />
              </div>
            )}
          </div>
        )}
        {(s.sidecarXml || s.sidecarJson) && (
          <div className="field"><label>Sidecar mapping</label>
            <span className="help">Leave empty to match names to fields by id or label (&lt;Title&gt; or "title", &lt;Description&gt;, &lt;Keywords&gt;…). Use a path such as Clip/Genre (XML) or programme/genre (JSON), or Clip/@name for an XML attribute. Map an element to “Asset ID” when your system names the asset in a different element than AssetId. <Link to="/help/sidecars">How sidecars are read</Link></span>
            {s.xmlMapping.map((m, i) => (
              <div key={i} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: 6, marginTop: 6 }}>
                <input className="input mono" value={m.element} placeholder="Element" onChange={(e) => set('xmlMapping', s.xmlMapping.map((x, j) => (j === i ? { ...x, element: e.target.value } : x)))} />
                <select className="select" value={m.fieldId} onChange={(e) => set('xmlMapping', s.xmlMapping.map((x, j) => (j === i ? { ...x, fieldId: e.target.value } : x)))}>
                  <option value={SIDECAR_ASSET_ID_TARGET}>Asset ID (fills a placeholder)</option>
                  <option value={SIDECAR_ASSET_CLASS_TARGET}>Asset class (a class’s name)</option>
                  <option value={SIDECAR_MASTER_VARIANT_TARGET}>Master variant (a name or code)</option>
                  <option value={SIDECAR_MASTER_OF_TARGET}>Master of (an asset ID)</option>
                  {fields.map((f) => <option key={f.id} value={f.id}>{f.label}</option>)}
                </select>
                <button className="btn ghost sm icon" aria-label="Remove mapping" onClick={() => set('xmlMapping', s.xmlMapping.filter((_, j) => j !== i))}><X size={13} /></button>
              </div>
            ))}
            <button className="btn ghost sm" style={{ alignSelf: 'flex-start', marginTop: 6 }} disabled={!fields.length}
              onClick={() => set('xmlMapping', [...s.xmlMapping, { element: '', fieldId: fields[0]!.id }])}><Plus size={13} /> Add mapping</button>
          </div>
        )}
        {rightsOn && schema && (
          <RightsDefaultsBlock schema={schema} defaults={s.defaults} onDefaults={(d) => set('defaults', d)} dates={s.rightsDates}
            onDates={(d) => set('rightsDates', d)} relativeTo="arrival" testId="watch-rights-block" />
        )}
        <div className="field"><label>Default metadata</label>
          <div className="segmented" role="radiogroup" aria-label="When a sidecar and these defaults both give a field" style={{ margin: '4px 0' }}>
            <button aria-pressed={s.sidecarPrecedence === 'sidecar'} onClick={() => set('sidecarPrecedence', 'sidecar')} data-testid="precedence-sidecar">Sidecars override defaults</button>
            <button aria-pressed={s.sidecarPrecedence === 'defaults'} onClick={() => set('sidecarPrecedence', 'defaults')} data-testid="precedence-defaults">Defaults override sidecars</button>
          </div>
          <span className="help">Applied to every asset from this folder. When a sidecar also gives a field, the choice above decides which wins; empty defaults never override a sidecar.</span>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 8, marginTop: 6 }}>
            {fields.filter((f) => f.id !== 'f_title' && !rightsIds.has(f.id)).map((f) => (

              <div className="field" key={f.id}><label style={{ fontSize: 12 }}>{f.label}</label>
                <DefaultValue field={f} value={s.defaults[f.id]} onChange={(v) => {
                  const next = { ...s.defaults };
                  if (v === undefined) delete next[f.id]; else next[f.id] = v;
                  set('defaults', next);
                }} />
              </div>
            ))}
          </div>
        </div>
      </div>
    </Modal>
  );
}
